import type { MenuLinks } from "./interfaces";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import ButtonPrimary from "../ButtonPrimary";
import ButtonSecondary from "../ButtonSecondary";

interface HighlightedButton {
  label: string;
  url: string;
  style: string;
}

interface MobileMenuProps {
  menuLinks: MenuLinks[];
  buttons: HighlightedButton[];
  onClose: () => void;
}

export const MobileMenu = ({ menuLinks, buttons, onClose }: MobileMenuProps) => {
  const [expandedLinkId, setExpandedLinkId] = useState<number | null>(null);

  const onMenuItemClick = (index: number) => {
    if (expandedLinkId === index) {
      setExpandedLinkId(null);
    } else {
      setExpandedLinkId(index);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Mobile navigation"
      className="fixed inset-x-0 bottom-0 top-16 z-50 h-[calc(100dvh-4rem)] w-screen overflow-hidden bg-slate-50 dark:bg-menu-dark"
    >
      <div className="mx-auto flex h-full w-full max-w-xl flex-col items-center overflow-y-auto px-6 pb-8 pt-16">
        <ul className="w-full shrink-0 text-center">
          {menuLinks.map((link, index) => (
            <div className="relative group" key={link.name}>
              <li
                className="px-3 py-4 font-semibold rounded-lg cursor-pointer text-center lg:px-4"
                onClick={() => onMenuItemClick(index)}
              >
                {link.link && (
                  <a href={link.link} onClick={onClose} className="flex items-center justify-center w-full">
                    <div className="flex items-center justify-center w-full text-gray-800 dark:text-gray-200">
                      <p className="text-xl font-bold">{link.name}</p>
                      {link.subLinks && link.subLinks.length > 0 && (
                        <div className="w-8 h-8 p-1">
                          {expandedLinkId === index ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </div>
                      )}
                    </div>
                  </a>
                )}
                {link.subLinks && link.subLinks.length > 0 && (
                  <div className="flex items-center justify-center w-full text-gray-800 dark:text-gray-200">
                    <p className="text-xl font-bold">{link.name}</p>
                    <div className="w-8 h-8 p-1">
                      {expandedLinkId === index ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </div>
                  </div>
                )}
                {expandedLinkId === index && (
                  <div className="w-full h-full">
                    <ul className="mt-4">
                      {link.subLinks &&
                        link.subLinks.map((subLink) => (
                          <li
                            className=" pl-1 py-4 font-semibold text-gray-800 rounded-lg cursor-pointer lg:px-4 dark:text-gray-200"
                            key={subLink.name}
                          >
                            <a href={subLink.link} onClick={onClose} className="flex items-center w-full">
                              <div className="w-10 h-10 p-1">{subLink.icon}</div>
                              <div className="flex flex-col ml-5">
                                <p className="font-bold">{subLink.name}</p>
                                <p className="text-xs text-gray-500">{subLink.description}</p>
                              </div>
                            </a>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </li>
            </div>
          ))}
        </ul>
        <div className="mt-auto flex w-full flex-col items-center gap-3 pt-8" onClick={onClose}>
          {buttons.map((button) =>
            button.style === "primary" ? (
              <ButtonPrimary
                key={button.url}
                url={button.url}
                label={button.label}
                className="w-full max-w-sm min-h-12"
              />
            ) : (
              <ButtonSecondary
                key={button.url}
                url={button.url}
                label={button.label}
                className="w-full max-w-sm min-h-12"
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};
